import { DataGridPro, DataGridProProps, GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { contactsColumnDefinitions } from './columns';
import { Contact } from '@api/Trinium/Endpoints/Contact/types';
import ContactsToolbar from './GridToolbar';
import { applyThemeGridSxOverrides } from '@/Infrastructure/Theme/AppThemeOverrides';
import { useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { GenericOverlay } from '../GenericNoRowsOverlay';
import EditIcon from '@mui/icons-material/EditTwoTone';
import ContactMobileCard from './MobileCard';
import BrandLinearProgress from '@/Infrastructure/Components/Loading/BrandLinearProgress';
import { useAppDispatch } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';

import styles from '../RenderCell.module.css';

interface ContactsDataGridProps {
	consigneeId: string;
	contacts?: Contact[];
}

export default function ContactsDataGrid({ consigneeId, contacts }: ContactsDataGridProps) {
	const appDispatch = useAppDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(() => theme.breakpoints.down('sm'));

	const handleOpenEditContactClick = useCallback(
		(contact: Contact) => {
			appDispatch(
				queueModal({
					Component: 'contactForm',
					Props: {
						consigneeId,
						contact
					}
				})
			);
		},
		[appDispatch, consigneeId]
	);

	/**
	 * The column to switch between viewing and editing a row. Needs to be defined here
	 * because of dependency on the rowModesModel of the Data Grid implementation.
	 */
	const actionsColumn: GridColDef<Contact> = useMemo(
		() => ({
			field: 'actions',
			type: 'actions',
			headerName: 'Edit',
			headerClassName: styles['bold'],
			width: 80,
			cellClassName: 'actions',
			getActions: ({ row }) => {
				return [
					<GridActionsCellItem
						icon={
							<EditIcon
								titleAccess='Edit Row'
								color='primary'
							/>
						}
						label='Inline Edit'
						onClick={() => {
							handleOpenEditContactClick(row);
						}}
					/>
				];
			}
		}),
		[handleOpenEditContactClick]
	);

	const gridColumns = useMemo(() => [...contactsColumnDefinitions, actionsColumn], [actionsColumn]);

	const mobileContactCardsDef: GridColDef<Contact>[] = useMemo(
		() => [
			{
				field: 'MobileCards',
				renderCell: ({ row }) => (
					<ContactMobileCard
						row={row}
						onEdit={handleOpenEditContactClick}
					/>
				),
				display: 'flex',
				flex: 1
			}
		],
		[handleOpenEditContactClick]
	);

	/**
	 * When we are in mobile resolution, these specific props are implemented
	 */
	const mobileGridProps: Partial<DataGridProProps<Contact>> = {
		columns: mobileContactCardsDef,
		columnHeaderHeight: 0,
		getRowHeight: (props) => (props.id === 0 ? 32 : 180),
		editMode: 'cell',
		sx: {
			...applyThemeGridSxOverrides(),
			'& .MuiDataGrid-columnHeaders': {
				borderBottom: 'none'
			},
			'& .MuiDataGrid-cell': {
				border: 'none'
			},
			'& .MuiDataGrid-row.Mui-hovered': {
				backgroundColor: 'inherit'
			},
			'& .MuiDataGrid-row:hover': {
				backgroundColor: 'inherit'
			},
			'& .MuiDataGrid-row.Mui-selected': {
				backgroundColor: 'transparent'
			},
			'& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
				backgroundColor: 'transparent'
			},
			'& .MuiDataGrid-row.Mui-selected:hover': {
				backgroundColor: 'transparent'
			}
		}
	};

	return (
		<DataGridPro
			columns={gridColumns}
			rows={contacts ?? []}
			columnVisibilityModel={{
				id: false
			}}
			columnHeaderHeight={60}
			getRowHeight={(props) => (props.id === 0 ? 32 : 50)}
			slots={{
				toolbar: ContactsToolbar,
				loadingOverlay: BrandLinearProgress,
				noRowsOverlay: () => <GenericOverlay overlayText='No Contacts Found.' />
			}}
			slotProps={{
				toolbar: {
					contactToolbarOverrides: {
						consigneeId
					}
				}
			}}
			className={styles['custom-data-grid']}
			sx={{
				...applyThemeGridSxOverrides()
			}}
			{...(isMobile ? mobileGridProps : {})}
		/>
	);
}
